import * as React from 'react'
import {
  Palette,
  Sun,
  Moon,
  Monitor,
  RotateCcw,
  Copy,
  Check,
  Square,
  Minimize2,
  Circle,
  Octagon,
  LayoutGrid,
  type LucideIcon,
} from 'lucide-react'
import {
  useTheme,
  type ColorMode,
  type BaseColor,
  type Radius,
  type Style,
  type Font,
} from '@/contexts/ThemeContext'
import { THEME_PRESETS } from '@/lib/theme-presets'
import { STYLES } from '@/lib/theme-styles'
import { THEME_COLORS } from '@/lib/theme-colors'
import { FONTS, preloadAllFonts } from '@/lib/theme-fonts'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { COPY_FEEDBACK_DURATION_MS } from '@/lib/constants'

const BASE_COLORS: { value: BaseColor; label: string; sample: string }[] = [
  { value: 'slate', label: 'Slate', sample: 'bg-slate-600' },
  { value: 'gray', label: 'Gray', sample: 'bg-gray-600' },
  { value: 'zinc', label: 'Zinc', sample: 'bg-zinc-600' },
  { value: 'neutral', label: 'Neutral', sample: 'bg-neutral-600' },
  { value: 'stone', label: 'Stone', sample: 'bg-stone-600' },
]

const RADIUS_OPTIONS: { value: Radius; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'sm', label: 'sm' },
  { value: 'default', label: 'md' },
  { value: 'lg', label: 'lg' },
]

const STYLE_ICONS: Record<string, LucideIcon> = {
  square: Square,
  minimize: Minimize2,
  circle: Circle,
  octagon: Octagon,
  grid: LayoutGrid,
}

export function ThemePanel() {
  const { theme, setTheme, setPreset, resetTheme, resolvedMode, exportTheme } = useTheme()
  const [isOpen, setIsOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  // Keyboard shortcut: Cmd/Ctrl + Shift + T
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 't') {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Preload fonts when panel opens
  React.useEffect(() => {
    if (isOpen) {
      preloadAllFonts()
    }
  }, [isOpen])

  const handleExport = async () => {
    const config = exportTheme()
    await navigator.clipboard.writeText(config)
    setCopied(true)
    setTimeout(() => setCopied(false), COPY_FEEDBACK_DURATION_MS)
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-4 right-4 z-50 h-10 w-10 rounded-full shadow-lg"
          title="Theme Settings (Cmd+Shift+T)"
        >
          <Palette className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-80 overflow-y-auto" overlayClassName="bg-transparent">
        <SheetHeader>
          <SheetTitle>Theme Settings</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Preset Selection */}
          <div className="space-y-2">
            <Label>Preset</Label>
            <Select
              value={theme.preset || 'custom'}
              onValueChange={(value) => {
                if (value !== 'custom') {
                  setPreset(value)
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a preset" />
              </SelectTrigger>
              <SelectContent>
                {THEME_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    <div className="flex flex-col">
                      <span>{preset.name}</span>
                      <span className="text-xs text-muted-foreground">{preset.description}</span>
                    </div>
                  </SelectItem>
                ))}
                {theme.preset === null && <SelectItem value="custom">Custom</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Style Selection */}
          <div className="space-y-3">
            <Label>Style</Label>
            <RadioGroup
              value={theme.style}
              onValueChange={(value) => setTheme({ style: value as Style })}
              className="space-y-2"
            >
              {STYLES.map((style) => {
                const IconComponent = STYLE_ICONS[style.icon] || Square
                return (
                  <div
                    key={style.id}
                    className={cn(
                      'flex items-start space-x-3 rounded-md border p-3 transition-colors',
                      theme.style === style.id
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent hover:bg-muted/50'
                    )}
                  >
                    <RadioGroupItem value={style.id} id={`style-${style.id}`} className="mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <Label
                        htmlFor={`style-${style.id}`}
                        className="flex cursor-pointer items-center gap-2 font-medium"
                      >
                        <IconComponent className="h-4 w-4 text-muted-foreground" />
                        {style.name}
                      </Label>
                      <p className="text-xs text-muted-foreground">{style.description}</p>
                    </div>
                  </div>
                )
              })}
            </RadioGroup>
          </div>

          <Separator />

          {/* Theme Color Selection */}
          <div className="space-y-3">
            <Label>Theme</Label>
            <div className="grid grid-cols-5 gap-2">
              {THEME_COLORS.map((color) => (
                <button
                  key={color.id}
                  onClick={() => setTheme({ themeColor: color.id })}
                  className={cn(
                    'group relative flex h-8 w-8 items-center justify-center rounded-full transition-all',
                    theme.themeColor === color.id
                      ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                      : 'hover:scale-110'
                  )}
                  title={color.name}
                >
                  <span
                    className="h-6 w-6 rounded-full"
                    style={{ backgroundColor: color.preview }}
                  />
                  {theme.themeColor === color.id && (
                    <Check className="absolute h-3 w-3 text-white drop-shadow-md" />
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Selected:{' '}
              <span className="font-medium">
                {THEME_COLORS.find((c) => c.id === theme.themeColor)?.name}
              </span>
            </p>
          </div>

          <Separator />

          {/* Base Color */}
          <div className="space-y-3">
            <Label>Base Color</Label>
            <RadioGroup
              value={theme.baseColor}
              onValueChange={(value) => setTheme({ baseColor: value as BaseColor })}
              className="grid grid-cols-2 gap-2"
            >
              {BASE_COLORS.map((color) => (
                <div key={color.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={color.value} id={`base-${color.value}`} />
                  <Label
                    htmlFor={`base-${color.value}`}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <div className={`h-4 w-4 rounded-full ${color.sample}`} />
                    {color.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Font Selection */}
          <div className="space-y-3">
            <Label>Font</Label>
            <Select
              value={theme.font}
              onValueChange={(value) => setTheme({ font: value as Font })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a font" />
              </SelectTrigger>
              <SelectContent>
                {FONTS.map((font) => (
                  <SelectItem key={font.id} value={font.id}>
                    <div className="flex flex-col">
                      <span style={{ fontFamily: font.family }}>{font.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="rounded-md border bg-muted/30 p-3">
              <p
                className="text-sm"
                style={{
                  fontFamily: FONTS.find((f) => f.id === theme.font)?.family,
                }}
              >
                {FONTS.find((f) => f.id === theme.font)?.preview}
              </p>
            </div>
          </div>

          <Separator />

          {/* Border Radius */}
          <div className="space-y-3">
            <Label>Radius</Label>
            <ToggleGroup
              type="single"
              value={theme.radius}
              onValueChange={(value) => {
                if (value) setTheme({ radius: value as Radius })
              }}
              className="justify-start"
            >
              {RADIUS_OPTIONS.map((option) => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  aria-label={`Radius ${option.label}`}
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <div className="flex gap-2">
              <div className="h-10 w-10 rounded-lg border-2 border-primary bg-card" />
              <div className="h-10 flex-1 rounded-lg border-2 border-primary bg-card" />
            </div>
          </div>

          <Separator />

          {/* Color Mode */}
          <div className="space-y-2">
            <Label>Color Mode</Label>
            <ToggleGroup
              type="single"
              value={theme.mode}
              onValueChange={(value) => {
                if (value) setTheme({ mode: value as ColorMode })
              }}
              className="justify-start"
            >
              <ToggleGroupItem value="light" aria-label="Light mode" className="gap-1.5">
                <Sun className="h-4 w-4" />
                Light
              </ToggleGroupItem>
              <ToggleGroupItem value="dark" aria-label="Dark mode" className="gap-1.5">
                <Moon className="h-4 w-4" />
                Dark
              </ToggleGroupItem>
              <ToggleGroupItem value="system" aria-label="System mode" className="gap-1.5">
                <Monitor className="h-4 w-4" />
                System
              </ToggleGroupItem>
            </ToggleGroup>
            {theme.mode === 'system' && (
              <p className="text-xs text-muted-foreground">Currently: {resolvedMode}</p>
            )}
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetTheme} className="flex-1">
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button variant="default" onClick={() => void handleExport()} className="flex-1">
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Export
                </>
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export function ThemePanelTrigger() {
  // Only render if feature flag is enabled
  const isEnabled = import.meta.env.VITE_ENABLE_THEME_PANEL === 'true'

  if (!isEnabled) return null

  return <ThemePanel />
}
